"use client";

import React from "react";
import { SpacingType, sectionVariants, ButtonStyleType  } from "@/types";
import { variantBackgroundColors } from "@/lib/utils";
import { SectionWrapper } from "../components/SectionWraper";
import { Button } from "@/components/ui/button";
import parse from "html-react-parser";
import Image from '@/components/ui/Image';
import Link from "next/link";

interface Hero16Props {
  darkMode?: boolean;
  spacing?: SpacingType;
  variant?: sectionVariants;
  contents?: {
    badge?: string;
    title?: string;
    content?: string;
    microCopy?: string;
    hasButton?: boolean;
    buttonText?: string;
    buttonLink?: string;
    buttonStyle?: ButtonStyleType;
    secondaryButton?: boolean;
    secondaryButtonText?: string;
    secondaryButtonLink?: string;
    secondaryButtonStyle?: ButtonStyleType;
    hasMediaBlocks?: boolean;
    mediaBlocks?: {
      image?: string;
      imageAltText?: string;
      label?: string;
      content?: string;
    };
    hasFeatureBlocks?: boolean;
    featureBlocksTitle?: string;
    featureBlocksContent?: string;
    featureBlocks?: {
      icon?: string;
      title?: string;
      content?: string;
    }[];
  };
  attributes?: React.HTMLProps<HTMLElement>;
}

const Hero16 = ({
  darkMode = false,
  spacing,
  variant = 'A',
  contents,
  attributes,
}: Hero16Props) => {

  const bgColor = variantBackgroundColors(darkMode, variant);

  return (
    <SectionWrapper 
      spacing={spacing}
      variant={variant}
      darkMode={darkMode}
      heroSection={true}
      componentName="hero16"
      attributes={attributes}
      usedInPageNav={false}
      pageNavTitle="Hero 16"
    >     
      <div className="container flex flex-col gap-28">
        <div className="flex flex-col gap-7 text-center lg:text-left items-center lg:items-start">
          {contents?.title && (
            <h1>{contents.title}</h1>
          )}
          {contents?.content && (
            <div className="paragraph-lg max-w-xl">
              {parse(contents.content)}
            </div>
          )}
          {contents?.hasButton && (
            <div className="mt-8 flex flex-col">
              <div className="flex flex-col gap-2 sm:flex-row">
                <div className="flex flex-col gap-2">
                  <div>
                    <Button 
                      size="lg" 
                      variant={contents?.buttonStyle || "primary"}
                      className="w-full gap-2 sm:w-auto"
                      asChild
                    >
                      <Link href={contents?.buttonLink || ""}>
                        {contents?.buttonText}
                      </Link>
                    </Button>
                  </div>
                  {contents?.microCopy && (
                    <p className="paragraph-sm text-muted-foreground text-center lg:text-left">
                      {contents?.microCopy}
                    </p>
                  )}
                </div>
                {contents?.secondaryButton && (
                  <Button
                    variant={contents?.secondaryButtonStyle || "outline"}
                    size="lg"
                    className="w-full gap-2 sm:w-auto"
                    asChild
                  >
                    <a href={contents?.secondaryButtonLink}>
                      {contents?.secondaryButtonText}
                    </a>
                  </Button>
                )}
              </div>
            </div>
          )}
        </div>
        {contents?.hasMediaBlocks && (
          <div className="flex flex-col gap-6 sm:grid sm:grid-cols-2">
            {contents?.mediaBlocks?.image && (
              <div className="flex flex-col gap-2 ">
                <Image
                  src={contents?.mediaBlocks?.image}
                  alt={contents?.mediaBlocks?.imageAltText || ""}
                  title={contents?.mediaBlocks?.imageAltText || ""}
                  className="size-full h-full rounded-2xl object-cover aspect-[656/384]"
                  width={656}
                  height={384}
                  priority
                  fetchPriority="high"
                />
              </div>
            )}
            <div className={`flex flex-col justify-between gap-25 rounded-2xl ${bgColor} p-10`}>
              {contents?.mediaBlocks?.label && (
                <p className="paragraph-base uppercase text-muted-foreground">{contents?.mediaBlocks?.label}</p>
              )}
              {contents?.mediaBlocks?.content && (
                <div className="paragraph-lg">{parse(contents?.mediaBlocks?.content)}</div>
              )}
            </div>
          </div>
        )}
        {contents?.hasFeatureBlocks && (
          <div className="flex flex-col gap-20">
            <div className="max-w-xl text-center sm:text-left">
              {contents?.featureBlocksTitle && (
                <h2>
                  {contents?.featureBlocksTitle}
                </h2>
              )}
              {contents?.featureBlocksContent && (
                <div className="paragraph-base text-muted-foreground mt-6">
                  {parse(contents?.featureBlocksContent)}
                </div>
              )}
            </div>
            <div className="grid gap-10 md:grid-cols-3">
              {contents?.featureBlocks?.map((block, index) => (
                <div className="flex flex-col" key={index}>
                  {block.icon && (
                  <div className={`mb-4 flex size-12 items-center justify-center rounded-2xl ${bgColor}`}>
                    <span className="material-symbols-outlined text-brand">
                      {block.icon}
                    </span>
                  </div>
                  )}
                  {block.title && (
                    <div className="paragraph-lg font-bold">
                      {block.title}
                    </div>
                  )}
                  {block.content && (
                    <div className="paragraph-base text-muted-foreground mt-1">
                      {parse(block.content)}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </SectionWrapper>
  );
};

export { Hero16 };    
